import { memo, useMemo, useEffect, useState, useRef } from "react";
import Image from "next/image";
import PropTypes from "prop-types";
import { getCloudfrontUrl } from "../utils/mediaHelper";
import { addDofollowToAnchors } from "../utils/modifyHtmlLinks";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import FreeConsultationForm from "./calendly-form-modal";
import Link from "next/link";

const toSpotifyEmbedUrl = (url) => {
  try {
    const cleanUrl = url?.trim();
    if (!cleanUrl) return null;

    const u = new URL(cleanUrl);
    const parts = u.pathname.split("/").filter(Boolean);
    const type = parts[0];
    const id = parts[1];

    if (!type || !id) return null;

    return `https://open.spotify.com/embed/${type}/${id}`;
  } catch {
    return null;
  }
};

const slugify = (str = "") =>
  str
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const SCROLL_OFFSET = 110; // match your sticky header/top spacing

const BlogDetails = memo(({ singleBlog, className = "" }) => {
  const { t } = useTranslation("blogs");
  const router = useRouter();
  const lang = router.locale || "en"; // kept
  const [mounted, setMounted] = useState(false);
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleClick = () => {
    setLiked(true);
    setOpen((p) => !p);
  };

  const openConsultationModal = () => {
    setIsConsultationOpen(true);
  };

  const closeConsultationModal = () => {
    setIsConsultationOpen(false);
  };

  // ✅ active section id for TOC highlight
  const [activeTocId, setActiveTocId] = useState("");

  // used to throttle scroll handler
  const rafRef = useRef(null);
  const heroRef = useRef(null);

  const podcast0 = useMemo(() => {
    const arr = singleBlog?.podcasts;
    return Array.isArray(arr) && arr.length ? arr[0] : null;
  }, [singleBlog?.podcasts]);

  const heroEmbedUrl = useMemo(() => {
    const url = podcast0?.spotifyEmbed?.trim();
    return url ? toSpotifyEmbedUrl(url) : null;
  }, [podcast0]);

  useEffect(() => setMounted(true), []);

  const cleanHTML = (html = "") => {
    const cleaned = (html || "")
      .replace(/\s+/g, " ")
      .replace(/<(\w+)[^>]*>\s*<\/\1>/g, "")
      .replace(/<\w+>\s*<br\s*\/?>\s*<\/\w+>/gi, "")
      .replace(/<p(?![^>]*class=)/gi, '<p class="m-0"')
      .replace(/<p class="/gi, '<p class="m-0 ');
    return addDofollowToAnchors(cleaned);
  };

  // ✅ single source of truth: does a section render?
  const isRenderableSection = (blogDetail) => {
    const cleanedDescription = cleanHTML(blogDetail?.headingDescription);

    const hasValidPoints = blogDetail?.points?.some((p) => {
      const titleOk = !!p?.title?.trim();
      const descOk = !!cleanHTML(p?.description)?.trim();
      return titleOk || descOk;
    });

    return Boolean(
      blogDetail?.heading?.trim() ||
      cleanedDescription?.trim() ||
      hasValidPoints ||
      blogDetail?.blogsectionimage?.url,
    );
  };

  /**
   * ✅ TOC generated from rendered blogDetails headings (NOT API)
   * - Only includes sections that actually render
   * - Only includes items that have a heading
   */
  const { tocItems, idByIndex } = useMemo(() => {
    const details = Array.isArray(singleBlog?.blogDetails)
      ? singleBlog.blogDetails
      : [];

    const usedIds = new Map();
    const map = new Map();
    const items = [];

    details.forEach((d, idx) => {
      if (!isRenderableSection(d)) return;

      const title = (d?.heading || "").trim();
      if (!title) return;

      const base = slugify(title) || `section-${idx}`;
      const count = (usedIds.get(base) || 0) + 1;
      usedIds.set(base, count);

      const id = count === 1 ? base : `${base}-${count}`;

      map.set(idx, id);
      items.push({ id, title, targetIndex: idx });
    });

    return { tocItems: items, idByIndex: map };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleBlog?.blogDetails]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    // ✅ set active immediately so highlight updates on click
    setActiveTocId(id);

    const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
    window.history.replaceState(null, "", `#${id}`);
  };

  const onTocClick = (e, id) => {
    e.preventDefault();
    scrollToId(id);
  };

  /**
   * ✅ Scroll-spy (robust): based on scroll position, not IntersectionObserver
   * Works reliably even for tall sections/images.
   */
  useEffect(() => {
    if (!mounted || tocItems.length === 0) return;

    const ids = tocItems.map((x) => x.id);
    const getElements = () =>
      ids
        .map((id) => ({
          id,
          el: document.getElementById(id),
        }))
        .filter((x) => x.el);

    let elements = getElements();

    const setFromScroll = () => {
      const scrollY = window.scrollY || 0;
      const marker = scrollY + SCROLL_OFFSET + 1;

      // find last section whose top <= marker
      let currentId = elements[0]?.id || "";

      for (const item of elements) {
        const top = item.el.offsetTop;
        if (top <= marker) currentId = item.id;
        else break;
      }

      if (currentId) setActiveTocId(currentId);
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setFromScroll();
      });
    };

    const onResize = () => {
      // re-grab elements in case layout changed (images loaded, etc.)
      elements = getElements();
      setFromScroll();
    };

    // initial: hash or first
    const hash = window.location.hash?.replace("#", "");
    if (hash && ids.includes(hash)) {
      setActiveTocId(hash);
      // also ensure correct after load
      requestAnimationFrame(() => setFromScroll());
    } else {
      requestAnimationFrame(() => setFromScroll());
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    // extra: update after images load can change offsets
    const imgTimer = setTimeout(onResize, 600);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      clearTimeout(imgTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [mounted, tocItems]);

  // ✅ keep active TOC item visible in TOC scroll
  useEffect(() => {
    if (!activeTocId) return;
    const activeLink = document.querySelector(`a[href="#${activeTocId}"]`);
    if (activeLink) activeLink.scrollIntoView({ block: "nearest" });
  }, [activeTocId]);

  const getYoutubeEmbed = (url) => {
    if (!url) return null;

    try {
      const parsed = new URL(url);

      // SHORTS
      if (parsed.pathname.includes("/shorts/")) {
        const id = parsed.pathname.split("/shorts/")[1].split("?")[0];
        return `https://www.youtube.com/embed/${id}`;
      }

      // youtu.be (FIXED)
      if (parsed.hostname === "youtu.be") {
        const id = parsed.pathname.slice(1); // safer than replace
        return `https://www.youtube.com/embed/${id}`;
      }

      // watch?v=
      const id = parsed.searchParams.get("v");
      if (id) {
        return `https://www.youtube.com/embed/${id}`;
      }

      return null;
    } catch {
      return null;
    }
  };

  // const getYoutubeEmbed = (url) => {
  //   try {
  //     const u = new URL(url);
  //     if (u.hostname.includes("youtu.be")) {
  //       return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
  //     }
  //     return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
  //   } catch {
  //     return null;
  //   }
  // };

  return (
    <section
      className={`self-stretch flex flex-col items-start justify-start
      py-8 lg:py-[60px]
      px-3 sm:px-6 lg:px-20
      box-border gap-6 lg:gap-10
      text-5xl text-text-primary max-w-full mx-auto ${className}`}
    >
      {podcast0 && (
        <>
          <p className="text-md m-0">
            {lang === "ar" ? "استمعوا الى البودكاست" : "Dive Into Our Podcast"}
          </p>
          <div
            ref={heroRef}
            className="w-full scroll-mt-24 mb-1 sm:px-4 lg:px-0"
          >
            <div className="w-full rounded-[16px] overflow-hidden bg-white">
              <div className="w-full h-[200px] sm:h-[240px] md:h-[266px] lg:h-[300px]">
                {heroEmbedUrl ? (
                  <iframe
                    key={podcast0._id}
                    title={podcast0.title || podcast0._id}
                    src={heroEmbedUrl}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    className="block w-full h-full rounded-[16px]"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center px-4 text-center">
                    <a
                      href={podcast0.spotifyEmbed?.trim()}
                      target="_blank"
                      rel="noreferrer"
                      className="underline break-words"
                    >
                      Open in Spotify
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="w-full lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
        {/* LEFT */}
        <div className="min-w-0">
          {/* Date + Read time */}
          {singleBlog?.postedDate && (
            <div className="mb-6 flex flex-row items-center justify-between w-full max-w-full gap-5">
              <div className="relative text-[16px] sm:text-[18px] leading-[140%] font-medium m-0 text-[#374151]">
                {lang === "ar" ? "آخر تحديث:" : "Last updated:"}{" "}
                {new Date(singleBlog?.postedDate).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </div>

              <div
                className="relative text-[16px] sm:text-[18px] leading-[140%] font-medium m-0 text-[#374151] whitespace-nowrap"
                dir="ltr"
              >
                {singleBlog?.timeToReadForBlog || "5 min read"}
              </div>
            </div>
          )}

          {/* Quick Answer */}
          {/* {singleBlog?.quickAnswer && (
            <div className="w-full box-border mb-10 rounded-[24px] border border-[#cfe0ff] bg-[#edf4ff] px-6 py-8 sm:px-8 sm:py-10">
              <h2 className="m-0 mb-5 text-[28px] sm:text-[34px] leading-[120%] max-w-[700px] text-[#111827]">
                Quick Answer
              </h2>
              <div
                className="m-0 text-[18px] sm:text-[20px] leading-[160%] text-[#374151] rich-text"
                dangerouslySetInnerHTML={{
                  __html: singleBlog?.quickAnswer,
                }}
              />
            </div>
          )} */}

          {/* Hero image */}
          {/* <div className="w-full sm:px-0 mb-10">
            <div className="relative w-full overflow-hidden rounded-none sm:rounded-3xl aspect-[1200/630] bg-gray-50">
              <Image
                fill
                src={
                  getCloudfrontUrl(singleBlog?.coverImage?.url) ||
                  "/blog-posts@3x.png"
                }
                alt={singleBlog?.coverImage?.alt || ""}
                className="object-contain object-center"
                sizes="100vw"
                priority
              />
            </div>
          </div> */}

          {/* Key takeaways */}
          {singleBlog?.keyTakeaways?.length > 0 && (
            <div className="w-full mb-8">
              <h3 className="m-0 mb-4 text-[24px] leading-[120%] font-semibold underline">
                {t("blogs.keyTakeaways")}
              </h3>
              <ul className="m-0">
                {singleBlog?.keyTakeaways?.map((item, ind) => (
                  <li
                    className="text-[18px] leading-[120%] font-semibold mb-2"
                    key={ind}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Intro description */}
          {mounted && singleBlog?.description && (
            <div
              className="text-[16px] leading-[150%] m-0 rich-text w-full break-words overflow-hidden mb-10 blog_des"
              dangerouslySetInnerHTML={{
                __html: cleanHTML(singleBlog.description || ""),
              }}
            />
          )}

          {/* slide show  */}
          {/* slide show */}
          {singleBlog?.slideshowlink && (
            <iframe
              src={singleBlog?.slideshowlink}
              width="100%"
              height="515"
              frameborder="0"
              marginwidth="0"
              marginheight="0"
              scrolling="no"
              style={{
                border: "var(--border-1) solid #CCC",
                borderWidth: "1px",
                marginBottom: "3rem",
                maxWidth: "100%",
              }}
              allowfullscreen
            ></iframe>
          )}

          {/* YouTube Video */}
          {singleBlog?.youtubelink && (
            <div className="w-full mb-10">
              <div className="relative w-full aspect-video rounded-2xl overflow-hidden">
                <iframe
                  src={getYoutubeEmbed(singleBlog?.youtubelink)}
                  title="YouTube video"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Sections */}
          {singleBlog?.blogDetails?.map((blogDetail, index) => {
            if (!isRenderableSection(blogDetail)) return null;

            const cleanedDescription = cleanHTML(
              blogDetail?.headingDescription,
            );

            const hasValidPoints = blogDetail?.points?.some(
              (p) => p?.title?.trim() || cleanHTML(p?.description),
            );

            // ✅ ID exists only when section is in TOC (has heading)
            const sectionId =
              idByIndex.get(index) ||
              slugify(blogDetail?.heading || `section-${index}`);

            return (
              <div
                key={index}
                // ✅ IMPORTANT: id is on the section container used by TOC
                id={sectionId}
                style={{ scrollMarginTop: SCROLL_OFFSET }}
                className={`self-stretch flex flex-col md:flex-col items-start md:items-start justify-start gap-6 md:gap-10 max-w-full mb-4 ${
                  index % 2 !== 0 ? "md:flex-row-reverse" : ""
                }`}
              >
                {(blogDetail?.heading?.trim() ||
                  cleanedDescription ||
                  hasValidPoints) && (
                  <div className="flex flex-col items-start justify-start flex-1 w-full gap-6">
                    {blogDetail?.heading && (
                      <h2 className="m-0 relative text-[24px] leading-[140%] font-bold capitalize">
                        {blogDetail?.heading}
                      </h2>
                    )}

                    {blogDetail?.blogsectionimageafterheading?.url && (
                      <div className="w-full">
                        <div className="relative w-full max-w-[920px] h-[250px] sm:h-[360px] lg:h-[480px] overflow-hidden rounded-2xl bg-white border border-black/10">
                          <Image
                            fill
                            className="object-contain"
                            src={
                              getCloudfrontUrl(
                                blogDetail?.blogsectionimageafterheading.url,
                              ) || "/blog-posts@3x.png"
                            }
                            alt={
                              blogDetail?.blogsectionimageafterheading?.alt ||
                              ""
                            }
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 920px, 920px"
                          />
                        </div>
                      </div>
                    )}

                    {cleanedDescription && (
                      <div
                        className="text-[16px] leading-[150%] rich-text w-full break-words overflow-hidden blog_des"
                        dangerouslySetInnerHTML={{
                          __html: cleanedDescription || "",
                        }}
                      />
                    )}

                    {blogDetail?.blogsectionimage?.url && (
                      <div className="w-full">
                        <div className="relative w-full max-w-[920px] h-[240px] sm:h-[320px] lg:h-[420px] overflow-hidden rounded-3xl bg-gray-50">
                          <Image
                            fill
                            src={
                              getCloudfrontUrl(
                                blogDetail.blogsectionimage.url,
                              ) || "/blog-posts@3x.png"
                            }
                            alt={blogDetail.blogsectionimage?.alt || ""}
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 920px, 920px"
                            priority={index === 0}
                            className="object-contain object-center"
                          />
                        </div>
                      </div>
                    )}

                    {hasValidPoints &&
                      blogDetail?.points?.map((point, ind) => {
                        const cleanedPointDesc = cleanHTML(point?.description);
                        const hasContent =
                          point?.title?.trim() ||
                          cleanedPointDesc ||
                          point?.blogsectionimage?.url;

                        if (!hasContent) return null;

                        return (
                          <div
                            className="flex flex-col items-start justify-start flex-1 w-full gap-4"
                            key={ind}
                          >
                            {point?.title && (
                              <h2 className="m-0 relative text-[20px] leading-[140%] font-medium capitalize">
                                {point?.title}
                              </h2>
                            )}

                            {point?.blogsectionimage?.url && (
                              <div className="w-full">
                                <div className="relative w-full max-w-[920px] h-[200px] sm:h-[260px] lg:h-[360px] overflow-hidden rounded-2xl bg-white border border-black/10">
                                  <Image
                                    fill
                                    className="object-contain"
                                    src={
                                      getCloudfrontUrl(
                                        point.blogsectionimage.url,
                                      ) || "/blog-posts@3x.png"
                                    }
                                    alt={point?.blogsectionimage?.alt || ""}
                                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 920px, 920px"
                                  />
                                </div>
                              </div>
                            )}

                            {cleanedPointDesc && (
                              <div
                                className="text-[16px] leading-[150%] rich-text w-full break-words overflow-hidden blog_des"
                                dangerouslySetInnerHTML={{
                                  __html: cleanedPointDesc || "",
                                }}
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Expert Insight */}
          {/* {singleBlog?.expertAnswer && (
            <div className="w-full box-border my-12 rounded-[24px] border border-[#f2dfaa] bg-[#fff7e6] px-6 py-8 sm:px-8 sm:py-10">
              <h2 className="m-0 mb-5 text-[28px] sm:text-[34px] leading-[120%] max-w-[700px] text-[#111827]">
                Expert Insight
              </h2>
              <div
                className="m-0 text-[18px] sm:text-[20px] leading-[160%] text-[#374151] rich-text"
                dangerouslySetInnerHTML={{
                  __html: singleBlog?.expertAnswer,
                }}
              />
            </div>
          )} */}

          {/* <div
            ref={wrapRef}
            className="fixed z-[9999999] flex flex-col items-end gap-2"
            style={{ bottom: "11rem", right: "2rem" }}
          >
          rounded-2xl border border-black/10 bg-white p-4 shadow-sm
            <div
              className={`
          w-[300px] rounded-2xl border border-black/10 bg-white p-4
          transition-all duration-200
          ${open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}
        `}
            >
              <p className="m-0 mb-2 text-[14px] font-semibold text-black">
                Enjoying this article?
              </p>
              <p className="m-0 mb-3 text-[13px] leading-relaxed text-black/80">
                Add Prism as a Preferred Source in Google Search so we show up
                in your AI Overviews.
              </p>
              <a
                href="https://www.google.com/preferences/source?q=prism-me.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full bg-crimson px-4 py-2 text-[13px] font-bold text-white no-underline hover:bg-crimson/90"
              >
                ⭐ Add Prism as Preferred Source
              </a>
            </div>

            <button
              onClick={handleClick}
              className={`
          flex items-center gap-2 rounded-full border px-5 py-2.5
          text-[14px] font-medium transition-all duration-150 shadow-sm
          ${
            liked
              ? "border-crimson bg-crimson text-white"
              : "border-black/15 bg-crimson text-white hover:bg-crimson/80"
          }
        `}
              aria-label="Like this article"
            >
              <span
                className={`text-[18px] transition-transform duration-200 ${
                  liked ? "scale-125" : ""
                }`}
              >
                👍
              </span>
              {liked ? "Thanks!" : "Liked this?"}
            </button>
          </div> */}

          <div className="fixed bottom-[11rem] right-[2rem] z-[9999999] flex flex-col items-end gap-2">
            <a
              href="https://www.google.com/preferences/source?q=prism-me.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setLiked(true)}
              className={`
          flex items-center gap-2 rounded-full border px-5 py-2.5
          text-[14px] font-medium transition-all duration-150 shadow-sm no-underline
          ${
            liked
              ? "border-crimson bg-crimson text-white"
              : "border-black/15 bg-crimson text-white hover:bg-crimson/80"
          }
        `}
            >
              <span
                className={`text-[18px] transition-transform duration-200 ${liked ? "scale-125" : ""}`}
              >
                👍
              </span>
              {liked
                ? lang === "ar"
                  ? "شكرًا!"
                  : "Thanks!"
                : lang === "ar"
                  ? "هل أعجبك هذا؟"
                  : "Liked this?"}
            </a>
          </div>

          {/* Author */}
          <div className="flex flex-col gap-6 lg:flex-row mt-12">
            <Link
              href={`/author/${singleBlog?.authors?.[0]?.slug}`}
              className="block no-underline text-inherit"
            >
              <Image
                src={
                  getCloudfrontUrl(singleBlog?.authors?.[0]?.image) ||
                  "/lovetto_nazareth.webp"
                }
                alt={singleBlog?.authors?.[0]?.imageAlt || "Lovetto Nazareth"}
                width={150}
                height={150}
                className="rounded-[16px]"
              />
            </Link>
            <div className="box-border w-full lg:w-2/3">
              <h2 className="m-0 mb-4 text-[24px] leading-[140%] font-medium">
                {t("blogs.author.main_title")}{" "}
                <Link
                  href={`/author/${singleBlog?.authors?.[0]?.slug}`}
                  className="inline-block no-underline text-inherit"
                >
                  {singleBlog?.authors?.[0]?.name || t("blogs.author.title")}
                </Link>
              </h2>
              <div
                className="m-0 relative text-[16px] leading-[150%] rich-text"
                dangerouslySetInnerHTML={{
                  __html:
                    singleBlog?.authors?.[0]?.description ||
                    t("blogs.author.description"),
                }}
              />
            </div>
          </div>
          {/* Need AI-ready SEO CTA */}
          {/* {singleBlog?.ctaSection?.ctatitle && (
            <div className="w-full box-border mt-12 rounded-[24px] bg-[#061f5c] px-6 py-10 sm:px-8 sm:py-12">
              <h2 className="m-0 mb-6 max-w-[700px] text-[30px] sm:text-[38px] leading-[115%] font-bold text-white">
                {singleBlog?.ctaSection?.ctatitle}
              </h2>

              <div
                className="m-0 mb-8 max-w-[620px] text-[18px] sm:text-[20px] leading-[160%] text-white/80 rich-text"
                dangerouslySetInnerHTML={{
                  __html: singleBlog?.ctaSection?.ctadescription,
                }}
              />

              <button
                onClick={openConsultationModal}
                className="inline-flex items-center justify-center rounded-full bg-[#f5a400] px-7 py-4 text-[16px] font-bold text-[#111827] no-underline hover:bg-[#ffb21a]"
              >
                Book a Free Consultation
              </button>
            </div>
          )} */}
        </div>

        <FreeConsultationForm
          isOpen={isConsultationOpen}
          onClose={closeConsultationModal}
        />

        {/* RIGHT: Sticky TOC */}
        {mounted && tocItems.length > 0 && (
          <aside className="hidden lg:block">
            <div className="sticky top-[110px] max-h-[calc(100vh-130px)] overflow-auto">
              <div className="text-[16px] tracking-[0.12em] font-semibold text-purple-600 mb-4">
                {t("blogs.tableOfContents")}
              </div>

              <ul className="m-0 p-0 list-none space-y-2">
                {tocItems.map((item) => {
                  const isActive = item.id === activeTocId;
                  return (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        onClick={(e) => onTocClick(e, item.id)}
                        className={[
                          "block rounded-xl px-4 py-2 text-[16px] leading-[140%] no-underline",
                          isActive
                            ? "bg-black/5 font-semibold"
                            : "hover:bg-black/5",
                        ].join(" ")}
                      >
                        {item.title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>
        )}
      </div>
    </section>
  );
});

BlogDetails.propTypes = {
  className: PropTypes.string,
};

export default BlogDetails;
